import React, { useEffect, useRef, useState } from "react";

/**
 * A live-drawing candlestick loop for the landing hero.
 *
 * • 32 candles at a time, right-most is the "live" candle whose close ticks
 *   around every 120ms until a new candle prints every ~900ms.
 * • Deterministic-ish random walk seeded from `Math.random` so each visitor
 *   gets a slightly different tape.
 * • Pure SVG so it stays sharp at any hero width and doesn't pull in a
 *   charting library.
 * • Bottom row draws matching volume bars.
 */

const N = 32;              // candles on screen
const TICK_MS = 120;       // intra-candle updates
const NEW_CANDLE_MS = 900; // spawn cadence

function nextCandle(prev) {
  const open = prev ? prev.c : 100;
  const drift = (Math.random() - 0.49) * 2.2;   // slight bull bias
  const range = 0.6 + Math.random() * 2.6;
  const high = open + Math.max(0, drift) + Math.random() * range;
  const low  = open + Math.min(0, drift) - Math.random() * range;
  const close = low + Math.random() * (high - low);
  const vol = 0.25 + Math.random() * 1;
  return { o: open, h: high, l: low, c: close, v: vol };
}

function useCandles() {
  const [candles, setCandles] = useState(() => {
    const arr = [];
    for (let i = 0; i < N; i++) arr.push(nextCandle(arr[arr.length - 1]));
    return arr;
  });
  const lastTickRef = useRef(0);

  useEffect(() => {
    const tickId = setInterval(() => {
      setCandles(prev => {
        const next = prev.slice();
        const last = { ...next[next.length - 1] };
        // Random walk the close within the current range
        const step = (Math.random() - 0.5) * 0.7;
        last.c = last.c + step;
        last.h = Math.max(last.h, last.c);
        last.l = Math.min(last.l, last.c);
        next[next.length - 1] = last;
        return next;
      });
    }, TICK_MS);

    const newId = setInterval(() => {
      setCandles(prev => {
        const next = prev.slice(1);
        next.push(nextCandle(next[next.length - 1]));
        return next;
      });
      lastTickRef.current = Date.now();
    }, NEW_CANDLE_MS);

    return () => { clearInterval(tickId); clearInterval(newId); };
  }, []);
  return candles;
}

export default function HeroAnimatedChart() {
  const candles = useCandles();
  const width = 900;
  const height = 520;
  const padX = 32;
  const chartH = 360;
  const chartTop = 80;
  const volTop = chartTop + chartH + 8;
  const volH = 60;

  // Y-scale
  const allHighs = candles.map(c => c.h);
  const allLows = candles.map(c => c.l);
  const yMin = Math.min(...allLows) - 1.5;
  const yMax = Math.max(...allHighs) + 1.5;
  const y = (v) => chartTop + (1 - (v - yMin) / (yMax - yMin)) * chartH;

  const cw = (width - padX * 2) / N;
  const bodyW = Math.max(3, cw * 0.62);

  const last = candles[candles.length - 1];
  const priceStr = last.c.toFixed(2);
  const change = ((last.c - candles[0].o) / candles[0].o) * 100;
  const upTrend = change >= 0;

  // Volume scale
  const vMax = Math.max(...candles.map(c => c.v)) * 1.05;

  return (
    <div className="relative w-full h-full bg-neutral-950 text-white" data-testid="hero-animated-chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full block" preserveAspectRatio="none">
        <defs>
          <linearGradient id="bgGlow" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#10b981" stopOpacity="0.10" />
            <stop offset="1" stopColor="#3b82f6" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="areaFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor={upTrend ? "#10b981" : "#ef4444"} stopOpacity="0.35" />
            <stop offset="1" stopColor={upTrend ? "#10b981" : "#ef4444"} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Background glow */}
        <rect x="0" y="0" width={width} height={height} fill="url(#bgGlow)" />

        {/* Horizontal grid */}
        {[0.15, 0.35, 0.55, 0.75, 0.95].map((f, i) => (
          <line key={i}
            x1={padX} x2={width - padX}
            y1={chartTop + chartH * f} y2={chartTop + chartH * f}
            stroke="rgba(255,255,255,0.05)" strokeDasharray="2 4" strokeWidth="1" />
        ))}

        {/* Area under the closes */}
        {(() => {
          const pts = candles.map((c, i) => [padX + i * cw + cw / 2, y(c.c)]);
          const d = "M " + pts.map(p => p.join(",")).join(" L ");
          const area = d + ` L ${padX + (N - 1) * cw + cw / 2},${chartTop + chartH} L ${padX + cw / 2},${chartTop + chartH} Z`;
          return (
            <>
              <path d={area} fill="url(#areaFill)" />
              <path d={d} fill="none" stroke={upTrend ? "#10b981" : "#ef4444"} strokeWidth="1.5" strokeLinejoin="round" />
            </>
          );
        })()}

        {/* Candles */}
        {candles.map((c, i) => {
          const up = c.c >= c.o;
          const col = up ? "#10b981" : "#ef4444";
          const x = padX + i * cw + (cw - bodyW) / 2;
          const yOpen = y(c.o);
          const yClose = y(c.c);
          const yHi = y(c.h);
          const yLo = y(c.l);
          const bodyTop = Math.min(yOpen, yClose);
          const bodyH = Math.max(1.5, Math.abs(yClose - yOpen));
          const cx = padX + i * cw + cw / 2;
          const isLast = i === candles.length - 1;
          return (
            <g key={i} opacity={isLast ? 1 : 0.95}>
              <line x1={cx} x2={cx} y1={yHi} y2={yLo} stroke={col} strokeWidth="1" />
              <rect x={x} y={bodyTop} width={bodyW} height={bodyH} fill={col} />
              {isLast && (
                <rect x={x - 2} y={bodyTop - 2} width={bodyW + 4} height={bodyH + 4}
                  fill="none" stroke={col} strokeWidth="1" opacity="0.6">
                  <animate attributeName="opacity" values="0.9;0.2;0.9" dur="1.2s" repeatCount="indefinite" />
                </rect>
              )}
            </g>
          );
        })}

        {/* Live price tag on right axis */}
        {(() => {
          const yP = y(last.c);
          const col = upTrend ? "#10b981" : "#ef4444";
          return (
            <g>
              <line x1={padX} x2={width - padX} y1={yP} y2={yP}
                stroke={col} strokeWidth="0.75" strokeDasharray="4 4" opacity="0.6" />
              <rect x={width - padX - 62} y={yP - 11} rx="3" ry="3" width="60" height="22" fill={col} />
              <text x={width - padX - 32} y={yP + 4} fontSize="12" fontWeight="700"
                textAnchor="middle" fill="#0a0a0a" fontFamily="ui-monospace, monospace">
                {priceStr}
              </text>
            </g>
          );
        })()}

        {/* Volume bars */}
        {candles.map((c, i) => {
          const up = c.c >= c.o;
          const col = up ? "#10b981" : "#ef4444";
          const x = padX + i * cw + (cw - bodyW) / 2;
          const bh = (c.v / vMax) * volH;
          return (
            <rect key={"v" + i} x={x} y={volTop + (volH - bh)} width={bodyW} height={bh}
              fill={col} opacity="0.55" />
          );
        })}

        {/* Header text — instrument, price, change */}
        <g>
          <circle cx="26" cy="30" r="4" fill="#10b981">
            <animate attributeName="opacity" values="1;0.25;1" dur="1.4s" repeatCount="indefinite" />
          </circle>
          <text x="40" y="34" fill="#fff" fontSize="18" fontWeight="700"
            fontFamily="ui-sans-serif, system-ui">TRADELOG · Live paper feed</text>
          <text x={width - padX - 200} y="30" fill="#a3a3a3" fontSize="11" letterSpacing="4"
            fontFamily="ui-sans-serif, system-ui">EURUSD · 1H</text>
          <text x={width - padX - 200} y="52" fill="#fff" fontSize="26" fontWeight="800"
            fontFamily="ui-monospace, monospace">{priceStr}</text>
          <text x={width - padX - 90} y="52" fill={upTrend ? "#10b981" : "#ef4444"} fontSize="14" fontWeight="700"
            fontFamily="ui-monospace, monospace">
            {upTrend ? "+" : ""}{change.toFixed(2)}%
          </text>
        </g>
      </svg>
    </div>
  );
}
